/**
 * `getPersistedAuthProvider` — stays app-side, whole.
 *
 * The `AuthProvider` enum moved to `@heidi/auth/contracts` (OpenSpec
 * `monorepo-auth-package` · S1) and is re-exported here name-intact, so every
 * existing `import { AuthProvider } from '@/utils/auth/persistedAuthProvider'`
 * and every `jest.mock` literal against this path keeps resolving unchanged.
 *
 * `getPersistedAuthProvider()` itself does NOT move: it reads the
 * redux-persist `persist:root` slice through `./readPersistedAuthSlice`, and
 * that reader is held back app-side for persisted-key manifest hygiene (see
 * the note in that file). Splitting the enum out is the "independently
 * extracted pure module" the `auth-package-delegates` spec permits — the enum
 * has no importer dependency on the persisted-read half.
 *
 * This file is intentionally dependency-free beyond that reader, so it stays
 * safe to import from the pre-rehydration paths without pulling `authModel`
 * in behind it.
 */
import { AuthProvider } from '@heidi/auth/contracts';

import { readPersistedAuthSlice } from './readPersistedAuthSlice';

export { AuthProvider };

/**
 * Synchronously reads authProvider directly from localStorage.
 * This bypasses Redux rehydration timing issues by reading the persisted
 * value before Redux has finished restoring state.
 *
 * Use this when you need to know the authProvider immediately (e.g., to prevent
 * race conditions where clearPersistedModels might be called incorrectly).
 */
export function getPersistedAuthProvider(): AuthProvider | undefined {
  return readPersistedAuthSlice<{ authProvider?: AuthProvider }>()?.authProvider;
}
